import PropTypes from 'prop-types';
import str from 'underscore.string';
import { Link, ModalTrigger2, ModalConfirm, LinkButton, Icon } from '@evertrue/et-components';
import EmptyStateMessage from 'components/lists/empty-state-message';
import colors from 'base/colors';
import EnrichmentConfidencePill from './enrichment-confidence-pill';

const getSocialIcon = (network) => {
  switch (network) {
    case 'facebook':
      return <Icon icon="facebook-circle" size={1} style={{ color: colors.facebookBlue }} />;
    case 'twitter':
      return <Icon icon="twitter-square-2023" iconKit="gt2" size={1} />;
    case 'instagram':
      return <Icon icon="instagram-rounded" size={1} style={{ color: colors.instagramPink }} />;
    case 'linkedin':
      return <Icon icon="linkedin-box" size={1} style={{ color: colors.linkedinBlue }} />;
    default:
      return <Icon icon="social" size={1} before />;
  }
};

const getPlatformName = (type) => {
  if (type === 'linkedin') return 'LinkedIn';
  return str.capitalize(type);
};

const SocialMediaTable = ({ profiles }) => {
  if (!profiles.length) {
    return <EmptyStateMessage hideIcon text="No enriched social media data available for this constituent" />;
  }

  return (
    <div className="social-media-table">
      <div className="social-media-table--header">
        <div className="social-media-table--subheader">Social Profiles</div>
      </div>
      <table className="social-media-table--table">
        <thead>
          <tr>
            <th className="social-media-table--th">Platform</th>
            <th className="social-media-table--th">Bio</th>
            <th className="social-media-table--th">Followers</th>
            <th className="social-media-table--th">Following</th>
            <th className="social-media-table--th">Confidence</th>
          </tr>
        </thead>
        <tbody>
          {profiles.map((profile) => (
            <tr key={profile.type + profile.url} className="social-media-table--row">
              <td className="social-media-table--td social-media-table--platform">
                <div className="social-media-table--platform-content">
                  <span className="social-media-table--icon">{getSocialIcon(profile.type)}</span>
                  {profile.url ? (
                    <Link
                      title={`View ${profile.type} profile`}
                      href={profile.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="social-media-table--platform-link"
                    >
                      {getPlatformName(profile.type)}
                    </Link>
                  ) : (
                    <span className="social-media-table--platform-name">{getPlatformName(profile.type)}</span>
                  )}
                </div>
              </td>
              <td className="social-media-table--td">
                {profile.bio ? (
                  <ModalTrigger2
                    closeOnOutsideClick
                    trigger={({ open }) => (
                      <LinkButton title={`View ${profile.type} bio`} onClick={open}>
                        View Bio
                      </LinkButton>
                    )}
                  >
                    {({ close }) => (
                      <ModalConfirm
                        header={`${getPlatformName(profile.type)} Bio`}
                        onCancel={close}
                        style={{ width: 500 }}
                      >
                        <div className="profile-enrichment--more-modal">{profile.bio}</div>
                      </ModalConfirm>
                    )}
                  </ModalTrigger2>
                ) : (
                  <span className="social-media-table--dash">-</span>
                )}
              </td>
              <td className="social-media-table--td">
                {profile.followers !== null && profile.followers !== undefined ? (
                  <span>{profile.followers.toLocaleString()}</span>
                ) : (
                  <span className="social-media-table--dash">-</span>
                )}
              </td>
              <td className="social-media-table--td">
                {profile.following !== null && profile.following !== undefined ? (
                  <span>{profile.following.toLocaleString()}</span>
                ) : (
                  <span className="social-media-table--dash">-</span>
                )}
              </td>
              <td className="social-media-table--td">
                <EnrichmentConfidencePill score={profile.confidence_score} />
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

SocialMediaTable.propTypes = {
  profiles: PropTypes.array.isRequired,
};

export default SocialMediaTable;
